import React, { useCallback, useId, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Tooltip } from "antd";
import radiatorThermostatImage from "./Images/radiator_thermostat_image.png";
import radiatorPredefinedValveImage from "./Images/radiator_predefined_valve_image.png";

// Layout constants
const CANVAS = { width: 400, height: 200 };

// Thermostat dial constants
const DIAL = {
  centerX: 55,
  centerY: 44,
  innerRadius: 24,
  outerRadius: 31,
  startAngle: 40,
  angleStep: 55,
  positions: 6,
};

// Thermostat valve body constants
const THERMO_VALVE = {
  bodyShadow: { x: 38, y: 78, width: 34, height: 74 },
  chamberShadow: { x: 8, y: 103, width: 94, height: 39 },
  body: { x: 40, y: 80, width: 30, height: 70 },
  chamber: { x: 10, y: 105, width: 90, height: 35 },
  verticalPipe: { x: 50, y: 90, width: 10, height: 40 },
  horizontalPipe: { x: 0, y: 115, width: 50, height: 15 },
  outletFlow: { x: 50, y: 115, width: 60, height: 15 },
  gate: { x: 50, baseY: 100, width: 10, height: 15, range: 15 },
};

// Preset valve selector constants
const PRESET = {
  x: 180,
  y: 80,
  centerX: 45,
  centerY: 45,
  radius: 30,
  innerRadius: 7,
  dotRadius: 29,
  whiteDotRadius: 8,
  labelX: 85,
  labelY: 52,
};

// Predefined valve icon constants
const VALVE_ICON = {
  width: 15,
  height: 35,
  stripes: [
    { yOffset: 3 },
    { yOffset: 15 },
    { yOffset: 27 },
  ],
  stripeHeight: 5,
};

// Color palette
const COLORS = {
  dialGray: "#D1D1D1",
  dialBorder: "#BABABA",
  valveDark: "rgb(150,150,150)",
  valveLight: "rgb(200,200,200)",
  flowRed: "#ca7575ff",
  gateBlack: "#6e6e6eff",
  coldBlue: "#0066CC",
  hotRed: "#CC0000",
  valveOrange: "#F5B027",
  valveRed: "#F54927",
  white: "#FFFFFF",
  gray: "gray",
};

/**
 * Renders a predefined valve icon with stripes
 */
const ValveIcon = ({ x, y, opacity = 0.6 }) => (
  <g opacity={opacity}>
    <rect
      x={x}
      y={y}
      width={VALVE_ICON.width}
      height={VALVE_ICON.height}
      fill={COLORS.valveOrange}
      stroke={COLORS.valveRed}
      strokeWidth={2}
    />
    {VALVE_ICON.stripes.map((stripe, index) => (
      <rect
        key={`stripe-${index}`}
        x={x}
        y={y + stripe.yOffset}
        width={VALVE_ICON.width}
        height={VALVE_ICON.stripeHeight}
        fill={COLORS.valveRed}
        stroke="none"
      />
    ))}
    <rect
      x={x}
      y={y}
      width={VALVE_ICON.width}
      height={VALVE_ICON.height}
      fill="none"
      stroke={COLORS.gray}
      strokeWidth={1}
      strokeDasharray="5"
    />
  </g>
);

ValveIcon.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  opacity: PropTypes.number,
};

/**
 * PredefinedValvePlusThermostatValve - A comprehensive valve control visualization
 * Combines a thermostat valve with temperature dial and a preset valve selector
 * 
 * @param {Object} props - Component props
 * @param {number} props.value - Selected preset valve position (1-6)
 * @param {Function} props.onChange - Callback when preset valve position changes
 * @param {Function} props.onShowHighlightes - Callback when mouse enters/leaves component
 * @param {Function} props.onStopAnimation - Callback when mouse enters/leaves preset selector
 * @param {number} [props.d=1] - Thermostat valve opening (0-1)
 * @param {number} [props.temp=20] - Temperature setting (10-30°C)
 * @param {boolean} [props.isHighlighted=false] - Whether to show info labels
 * @param {string} [props.className] - Optional CSS class
 * @param {boolean} [props.showFlowAnimation=false] - Whether to show animated flow bubbles
 * @param {number} [props.flowRateLPM=0] - Flow rate in liters per minute (controls animation speed)
 * @param {boolean} [props.isRunning=false] - Whether the simulation is running (animation only plays when true)
 */
export function PredefinedValvePlusThermostatValve({
  value,
  onChange,
  onShowHighlightes,
  onStopAnimation,
  onTemperatureChange,
  d = 1,
  temp = 20,
  isHighlighted = false,
  className = "",
  showFlowAnimation = false,
  flowRateLPM = 0,
  isRunning = false,
}) {
  const { t } = useTranslation();
  const gradientId = useId();

  // Calculate flow rate based on preset valve position
  const valveLPM = useMemo(() => {
    const valveData = {
      1: 2.108,
      2: 4.743,
      3: 8.433,
      4: 12.649,
      5: 16.338,
      6: 19.501,
      7: 22.136
    };
    return valveData[value] || 0;
  }, [value]);

  // Memoize complex calculations
  const calculations = useMemo(() => {
    const distance = Math.min(1, Math.max(0, d));
    console.log("PredefinedValvePlusThermostatValve calculations d=" + d + " distance=" + distance);
    const gatePosition = THERMO_VALVE.gate.baseY + THERMO_VALVE.gate.range * (1 - distance);
    const normalizedTemp = 0.25 * temp - 2.5;

    // Temperature indicator arrow calculations
    const angleRad = (DIAL.startAngle + normalizedTemp * DIAL.angleStep - 90) * (Math.PI / 180);
    const headX = DIAL.centerX + DIAL.outerRadius * 0.7 * Math.cos(angleRad);
    const headY = DIAL.centerY + DIAL.outerRadius * 0.7 * Math.sin(angleRad);

    const baseWidth = 4;
    const baseAngle1 = angleRad + Math.PI / 2;
    const baseAngle2 = angleRad - Math.PI / 2;
    const baseRadius = DIAL.outerRadius * 0.2;

    const baseX1 = DIAL.centerX + baseRadius * Math.cos(angleRad) + baseWidth * Math.cos(baseAngle1);
    const baseY1 = DIAL.centerY + baseRadius * Math.sin(angleRad) + baseWidth * Math.sin(baseAngle1);
    const baseX2 = DIAL.centerX + baseRadius * Math.cos(angleRad) + baseWidth * Math.cos(baseAngle2);
    const baseY2 = DIAL.centerY + baseRadius * Math.sin(angleRad) + baseWidth * Math.sin(baseAngle2);

    return {
      distance,
      gatePosition,
      arrowPoints: `${DIAL.centerX},${DIAL.centerY} ${baseX1},${baseY1} ${headX},${headY} ${baseX2},${baseY2}`,
    };
  }, [d, temp]);

  // Memoize dial positions
  const dialPositions = useMemo(() => {
    return Array.from({ length: DIAL.positions }, (_, i) => {
      const angle = (DIAL.startAngle + i * DIAL.angleStep - 90) * (Math.PI / 180);
      return {
        index: i,
        bgX: DIAL.centerX + DIAL.innerRadius * 0.9 * Math.cos(angle),
        bgY: DIAL.centerY + DIAL.innerRadius * 0.9 * Math.sin(angle),
        textX: DIAL.centerX + DIAL.innerRadius * Math.cos(angle),
        textY: DIAL.centerY + DIAL.innerRadius * Math.sin(angle),
        rotation: DIAL.startAngle + i * DIAL.angleStep - 90,
        color: i < 3 ? COLORS.coldBlue : COLORS.hotRed,
      };
    });
  }, []);

  // Memoize preset valve dot positions
  const presetDotPositions = useMemo(() => {
    return Array.from({ length: 6 }, (_, i) => {
      const angle = (60 * i * Math.PI) / 180;
      return {
        index: i,
        x: PRESET.centerX + Math.sin(angle) * PRESET.dotRadius,
        y: PRESET.centerY + Math.cos(angle) * PRESET.dotRadius,
      };
    });
  }, []);

  // Flow animation: duration inversely proportional to flow rate (same logic as RoomUnit)
  const flowAnimationDuration = useMemo(() => {
    if (!showFlowAnimation || !isRunning) return 0;
    const rate = parseFloat(flowRateLPM);
    if (rate <= 0.05) return 0;
    // Higher flow rate → shorter duration → faster arrows
    return Math.max(0.4, 18 / rate);
  }, [flowRateLPM, showFlowAnimation, isRunning]);

  const flowBubbleCount = 5;

  // Flow paths through the valve system (reversed: right to left)
  // Thermostat valve flow: outlet (right) through gate to inlet (left)
  // Path goes through the horizontal pipe at y=122 (center of 115-130)
  const thermostatFlowPath = useMemo(() => {
    return `M 110,122 L 55,122 L 50,122 L 0,122`;
  }, []);

  // Output valve flow path (reversed: right to left)
  const outputFlowPath = useMemo(() => {
    return `M 385,122 L 335,122`;
  }, []);

  // Generate stable random offsets for bubbles (computed once per bubble index)
  const bubbleOffsets = useMemo(() => {
    return Array.from({ length: flowBubbleCount }, (_, i) => ({
      yOffset: (((i * 13 + 5) % 7) - 3) * 1.5, // Y offset between -4.5 and +4.5
    }));
  }, []);

  // Event handlers
  const handleMouseEnter = useCallback(() => {
    if (onShowHighlightes) onShowHighlightes(true);
  }, [onShowHighlightes]);

  const handleMouseLeave = useCallback(() => {
    if (onShowHighlightes) onShowHighlightes(false);
  }, [onShowHighlightes]);

  const handlePresetMouseEnter = useCallback(() => {
    if (onStopAnimation) onStopAnimation(true);
  }, [onStopAnimation]);

  const handlePresetMouseLeave = useCallback(() => {
    if (onStopAnimation) onStopAnimation(false);
  }, [onStopAnimation]);

  const handlePresetClick = useCallback(() => {
    if (onChange) {
      const nextValue = (value) >= 7 ? 1 : value + 1;
      onChange(nextValue);
    }
  }, [value, onChange]);

  const handleDialClick = useCallback(() => {
    if (onTemperatureChange) {
      const nextTemp = temp >= 30 ? 10 : temp + 1;
      onTemperatureChange(nextTemp);
    }
  }, [temp, onTemperatureChange]);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={CANVAS.width}
      height={CANVAS.height}
      viewBox={`0 0 ${CANVAS.width} ${CANVAS.height}`}
      className={className}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="group"
      aria-label="Valve control system"
    >
      {/* Canvas background */}
      <rect
        x={0}
        y={0}
        width={CANVAS.width}
        height={CANVAS.height}
        fill="transparent"
        stroke="transparent"
        strokeWidth={1}
      />

      {/* Thermostat Valve Section */}
      <g id="thermostat-valve">
        {/* Temperature dial */}
        <g id="temperature-dial">
          <circle
            cx={DIAL.centerX}
            cy={DIAL.centerY}
            r={DIAL.outerRadius}
            fill="none"
            stroke={COLORS.gray}
            strokeWidth={2}
          />
          <circle
            cx={DIAL.centerX}
            cy={DIAL.centerY}
            r={DIAL.outerRadius + 2}
            fill="none"
            stroke="rgba(0,0,0,0.2)"
            strokeWidth={1}
          />
          <circle
            cx={DIAL.centerX}
            cy={DIAL.centerY}
            r={DIAL.innerRadius}
            fill="none"
            stroke="rgba(0,0,0,0.1)"
            strokeWidth={1}
          />
         
          {/* Dial position markers */}
          {dialPositions.map(({ index, bgX, bgY, textX, textY, rotation, color }) => (
            <g key={`dial-pos-${index}`}>
              <circle cx={bgX} cy={bgY} r={8} fill={COLORS.white} />
              <g transform={`rotate(${rotation}, ${textX}, ${textY})`}>
                <text
                  x={textX}
                  y={textY}
                  textAnchor="middle"
                  fontSize="14"
                  fontWeight="500"
                  fill={color}
                  dominantBaseline="central"
                >
                  {index}
                </text>
              </g>
            </g>
          ))}

          {/* Center pivot and indicator arrow */}
          <circle
            cx={DIAL.centerX}
            cy={DIAL.centerY}
            r={5}
            fill={COLORS.dialGray}
            stroke={COLORS.dialBorder}
            strokeWidth={1}
          />
          <polygon
            points={calculations.arrowPoints}
            fill={COLORS.dialGray}
            stroke={COLORS.dialBorder}
            strokeWidth={1}
          />
        </g>

        {/* Valve body structure */}
        <g id="valve-body">
          {/* Shadows */}
          <rect {...THERMO_VALVE.bodyShadow} rx={5} fill={COLORS.valveDark} />
          <rect {...THERMO_VALVE.chamberShadow} rx={5} fill={COLORS.valveDark} />

          {/* Main body */}
          <rect {...THERMO_VALVE.body} rx={5} fill={COLORS.valveLight} />
          <rect {...THERMO_VALVE.chamber} rx={5} fill={COLORS.valveLight} />

          {/* Internal piping */}
          <rect {...THERMO_VALVE.verticalPipe} fill={COLORS.white} />
          <rect {...THERMO_VALVE.horizontalPipe} fill={COLORS.white} />

          {/* Flow visualization */}
          <rect {...THERMO_VALVE.outletFlow} fill={COLORS.flowRed} />

          {/* Pipe edges */}
          <rect x={80} y={110} width={30} height={5} fill={COLORS.valveDark} />
          <rect x={80} y={130} width={30} height={5} fill={COLORS.valveDark} />
          <rect x={0} y={110} width={30} height={5} fill={COLORS.valveDark} />
          <rect x={0} y={130} width={30} height={5} fill={COLORS.valveDark} />

          {/* Inlet flow */}
          <rect
            x={0}
            y={115}
            width={55}
            height={15}
            fill={COLORS.flowRed}
            opacity={calculations.distance}
          />

          {/* Control gate */}
          <rect
            x={THERMO_VALVE.gate.x}
            y={calculations.gatePosition}
            width={THERMO_VALVE.gate.width}
            height={THERMO_VALVE.gate.height}
            rx={2}
            fill={COLORS.gateBlack}
          />

          {/* Predefined valve icon on thermostat */}
          <ValveIcon x={110} y={105} />
        </g>
        
        {/* Temperature dial clickable overlay with tooltip */}
        <Tooltip 
          title={<img src={radiatorThermostatImage} alt="Radiator Thermostat" style={{ maxWidth: '200px', display: 'block' }} />}
          color="white"
        >
          <circle
            cx={DIAL.centerX}
            cy={DIAL.centerY}
            r={DIAL.outerRadius + 5}
            fill="transparent"
            onClick={handleDialClick}
            style={{ cursor: onTemperatureChange ? 'pointer' : 'default' }}
          />
        </Tooltip>
      </g>

      {/* Preset Valve Selector */}
      <g
        id="preset-valve-selector"
        transform={`translate(${PRESET.x}, ${PRESET.y})`}
        className="hq-clickable"
        onMouseEnter={handlePresetMouseEnter}
        onMouseLeave={handlePresetMouseLeave}
        onClick={handlePresetClick}
        style={{ cursor: "pointer" }}
        role="button"
        tabIndex={0}
        aria-label={`Preset valve position ${value}`}
      >
        <defs>
          <radialGradient id={gradientId} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={COLORS.valveOrange} />
            <stop offset="100%" stopColor={COLORS.valveRed} />
          </radialGradient>
        </defs>

        {/* Main dial */}
        <circle
          cx={PRESET.centerX}
          cy={PRESET.centerY}
          r={PRESET.radius}
          stroke="orange"
          strokeWidth={2}
          fill={`url(#${gradientId})`}
        />
        <circle
          cx={PRESET.centerX}
          cy={PRESET.centerY}
          r={PRESET.innerRadius}
          stroke={COLORS.valveOrange}
          strokeWidth={2}
          fill={COLORS.valveOrange}
        />

        {/* Position markers */}
        {presetDotPositions.map(({ index, x, y }) => (
          <circle key={`preset-dot-${index}`} cx={x} cy={y} fill={COLORS.white} r={PRESET.whiteDotRadius} />
        ))}

        {/* Current value display with navigation indicators */}
        <g id="value-display">
          {/* Current value */}
          <text
            x={PRESET.labelX}
            y={PRESET.labelY}
            fontSize={22}
            fontWeight="bold"
            fill={COLORS.valveRed}
          >
            {value}
          </text>
          
          {/* Up arrow - right of text, centered above midline */}
          <path
            d="M 102 34 L 108 34 L 105 28 Z"
            fill="#999999"
            opacity={0.7}
          />
          
          {/* Down arrow - right of text, centered below midline */}
          <path
            d="M 102 55 L 108 55 L 105 61 Z"
            fill="#999999"
            opacity={0.7}
          />
          
          {/* Next value (above) */}
          <text
            x={PRESET.labelX + 6}
            y={PRESET.labelY - 21}
            fontSize={ 13 }
            fontWeight="400"
            fill="#747373ff"
            opacity={0.6}
            textAnchor="middle"
          >
            {value >= 7 ? 1 : value + 1}
          </text>
          
          {/* Previous value (below) */}
          <text
            x={PRESET.labelX + 6}
            y={PRESET.labelY + 13}
            fontSize={13}
            fontWeight="400"
            fill="#747373ff"
            opacity={0.6}
            textAnchor="middle"
          >
            {value <= 1 ? 7 : value - 1}
          </text>
        </g>
        
        {/* Hover area for tooltip - placed last to be on top */}
        <Tooltip 
          title={<img src={radiatorPredefinedValveImage} alt="Radiator Predefined Valve" style={{ maxWidth: '200px', display: 'block' }} />}
          color="white"
          placement="bottom"
        >
          <rect 
            x={0} 
            y={0} 
            width={90} 
            height={90} 
            fill="transparent" 
            style={{ cursor: 'pointer' }}
          />
        </Tooltip>
      </g>

      {/* Output Valve Section */}
      <g id="output-valve" transform="translate(320, 80)">
        <rect x={15} y={30} width={50} height={5} fill={COLORS.valveDark} />
        <rect x={15} y={50} width={50} height={5} fill={COLORS.valveDark} />
        <rect x={15} y={35} width={50} height={15} fill={COLORS.flowRed} />
        <ValveIcon x={0} y={25} />
      </g>

      {/* Connection lines */}
      <g id="connection-lines">
        <line
          x1={190}
          x2={129}
          y1={95}
          y2={103}
          strokeWidth={1}
          stroke={COLORS.gray}
          strokeDasharray="5"
        />
        <line
          x1={190}
          x2={129}
          y1={155}
          y2={142}
          strokeWidth={1}
          stroke={COLORS.gray}
          strokeDasharray="5"
        />
        <line
          x1={255}
          x2={316}
          y1={95}
          y2={103}
          strokeWidth={1}
          stroke={COLORS.gray}
          strokeDasharray="5"
        />
        <line
          x1={255}
          x2={316}
          y1={155}
          y2={142}
          strokeWidth={1}
          stroke={COLORS.gray}
          strokeDasharray="5"
        />
      </g>

      {/* Animated Flow Bubbles - Thermostat Valve Section */}
      {flowAnimationDuration > 0 && (
        <g id="thermostat-flow-bubbles" style={{ opacity: calculations.distance }}>
          <defs>
            <radialGradient id="bubbleGradientValve" cx="30%" cy="30%" r="70%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.95)" />
              <stop offset="40%" stopColor="rgba(255,255,255,0.6)" />
              <stop offset="100%" stopColor="rgba(255,200,200,0.3)" />
            </radialGradient>
          </defs>
          {Array.from({ length: flowBubbleCount }, (_, i) => {
            const offset = i / flowBubbleCount;
            // Add small randomness: ±15% variation per bubble (same logic as RoomUnit)
            const randomFactor = 1 + (((i * 7 + 3) % 11) - 5) * 0.03;
            const duration = flowAnimationDuration * randomFactor;
            const radiusRandom = 1.2 + (((i * 5 + 2) % 7) * 0.1);
            const yOffset = bubbleOffsets[i].yOffset;
            return (
              <circle
                key={`valve-bubble-${i}`}
                r={radiusRandom}
                fill="url(#bubbleGradientValve)"
                stroke="rgba(255,255,255,0.5)"
                strokeWidth={0.2}
                style={{ transform: `translateY(${yOffset}px)` }}
              >
                <animateMotion
                  dur={`${duration}s`}
                  repeatCount="indefinite"
                  begin={`-${offset * flowAnimationDuration}s`}
                  path={thermostatFlowPath}
                  calcMode="linear"
                />
              </circle>
            );
          })}
        </g>
      )}

      {/* Animated Flow Bubbles - Output Valve Section */}
      {flowAnimationDuration > 0 && (
        <g id="output-flow-bubbles" style={{ opacity: calculations.distance }}>
          {Array.from({ length: flowBubbleCount }, (_, i) => {
            const offset = i / flowBubbleCount;
            // Add small randomness: ±15% variation per bubble (same logic as RoomUnit)
            const randomFactor = 1 + (((i * 11 + 7) % 9) - 4) * 0.035;
            const duration = flowAnimationDuration * 0.5 * randomFactor;
            const radiusRandom = 1.0 + (((i * 3 + 1) % 5) * 0.1);
            const yOffset = bubbleOffsets[i].yOffset;
            return (
              <circle
                key={`output-bubble-${i}`}
                r={radiusRandom}
                fill="url(#bubbleGradientValve)"
                stroke="rgba(255,255,255,0.5)"
                strokeWidth={0.2}
                style={{ transform: `translateY(${yOffset}px)` }}
              >
                <animateMotion
                  dur={`${duration}s`}
                  repeatCount="indefinite"
                  begin={`-${offset * flowAnimationDuration * 0.5}s`}
                  path={outputFlowPath}
                  calcMode="linear"
                />
              </circle>
            );
          })}
        </g>
      )}

      {/* Info labels (conditional) */}
      {isHighlighted && (
        <g id="info-labels">
          <text x={195} y={60} fontSize={12} fill={COLORS.gray}>
            {t("heatPumpSimulation.room.presetValveLabel")}
          </text>
          <text x={170} y={80} fontSize={12} fill={COLORS.gray}>
            {valveLPM.toFixed(2)} L/min  (100 mBar)
          </text>
          <text x={10} y={165} fontSize={12} fill={COLORS.gray}>
            {t("heatPumpSimulation.room.thermostatValveLabel")}
          </text>
          <text x={38} y={180} fontSize={12} fill={COLORS.gray}>
            {(100 * d).toFixed(1)}%
          </text>
        </g>
      )}
    </svg>
  );
}

PredefinedValvePlusThermostatValve.propTypes = {
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  onShowHighlightes: PropTypes.func,
  onStopAnimation: PropTypes.func,
  onTemperatureChange: PropTypes.func,
  d: PropTypes.number,
  temp: PropTypes.number,
  isHighlighted: PropTypes.bool,
  className: PropTypes.string,
  showFlowAnimation: PropTypes.bool,
  flowRateLPM: PropTypes.number,
  isRunning: PropTypes.bool,
};

export default React.memo(PredefinedValvePlusThermostatValve);